import { call, fork, put, take } from "redux-saga/effects"

import { SYNC_ARCHIVIO } from "./actionTypes"

import {
  syncArchivioProgress,
  syncArchivioSuccess,
  syncArchivioFail,
} from "./actions"

import { getArchivioPage } from "../../helpers/backend_helper"
import {
  SCHEMA_VERSION,
  loadArchivio,
  loadMeta,
  saveArchivio,
  saveMeta,
  setCachedArchivio,
  mergeDelta,
  currentOwner,
  nowTs,
  clearLegacyArchivio,
} from "../../helpers/archivio_store"

const PAGE_SIZE = 1000;
// Nel download completo si salva su IndexedDB ogni N pagine (e all'ultima): riscrivere tutto l'array a ogni pagina
// costava in modo quadratico. Se si interrompe, al massimo si riscaricano N-1 pagine.
const SAVE_EVERY_PAGES = 5;
// Ogni tanto si riscarica tutto per sicurezza; negli altri casi basta il delta
const FULL_EVERY = 7 * 24 * 60 * 60;

// Download completo, pagina per pagina dal più recente al più vecchio. Salva ogni SAVE_EVERY_PAGES pagine, così se si
// interrompe (connessione persa, app chiusa) al giro successivo riprende dall'ultimo salvataggio.
function* downloadFull(tipo, meta, records, resume) {
  let beforeId = resume ? meta.beforeId : 0;
  let since = resume ? meta.since : null;
  let acc = resume ? records : [];
  // Ultimo stato salvato su IndexedDB, coerente con meta.beforeId
  let saved = resume ? records : null;
  let page = 0;
  try {
    do {
      const response = yield call(getArchivioPage, tipo, { beforeId, limit: PAGE_SIZE });
      // server_ts della prima pagina: il delta successivo recupera ciò che è cambiato durante il download
      if (since === null) since = response.server_ts;
      acc = acc.concat(response.data);
      beforeId = response.next_before_id || null;
      const done = beforeId === null;
      page++;
      if (!done && page % SAVE_EVERY_PAGES !== 0) {
        setCachedArchivio(tipo, acc);
        yield put(syncArchivioProgress(tipo, acc.length));
        continue;
      }
      yield call(saveArchivio, tipo, acc, {
        schemaVersion: SCHEMA_VERSION,
        owner: currentOwner(),
        since,
        partial: !done,
        beforeId,
        lastFull: done ? nowTs() : meta.lastFull || 0,
        lastSync: done ? nowTs() : meta.lastSync || null,
      });
      saved = acc;
      yield put(syncArchivioProgress(tipo, acc.length));
    } while (beforeId !== null);
  } catch (error) {
    // La cache in memoria non deve contenere pagine non salvate, altrimenti il resume le duplicherebbe
    setCachedArchivio(tipo, saved);
    throw error;
  }
  return acc;
}

// Delta dall'ultimo server_ts: solo record modificati + id eliminati
function* downloadDelta(tipo, meta, records) {
  let beforeId = 0;
  let serverTs = null;
  let deleted = [];
  let changed = [];
  do {
    const response = yield call(getArchivioPage, tipo, { beforeId, since: meta.since, limit: PAGE_SIZE });
    if (serverTs === null) {
      serverTs = response.server_ts;
      deleted = response.deleted || [];
    }
    changed = changed.concat(response.data);
    beforeId = response.next_before_id || null;
  } while (beforeId !== null);

  const newMeta = { ...meta, since: serverTs, lastSync: nowTs() };
  if (changed.length > 0 || deleted.length > 0) {
    records = mergeDelta(records, changed, deleted);
    yield call(saveArchivio, tipo, records, newMeta);
  } else {
    yield call(saveMeta, tipo, newMeta);
  }
  return records;
}

function* syncArchivio({ payload: { tipo, forceFull } }) {
  try {
    const meta = yield call(loadMeta, tipo);
    const records = yield call(loadArchivio, tipo);
    if (!navigator.onLine) {
      yield put(syncArchivioSuccess(tipo, records ? records.length : 0, meta.lastSync || null));
      return;
    }
    const valid = records && meta.schemaVersion === SCHEMA_VERSION && meta.owner === currentOwner();
    // Un download interrotto con la versione precedente (cursore crescente) non si riprende: riparte da capo
    const resume = valid && meta.partial && meta.beforeId > 0 && !forceFull;
    const full = forceFull || !valid || meta.partial || !meta.since || (meta.lastFull || 0) + FULL_EVERY < nowTs();

    const result = full
      ? yield call(downloadFull, tipo, valid ? meta : {}, records, resume)
      : yield call(downloadDelta, tipo, meta, records);

    yield put(syncArchivioSuccess(tipo, result.length, nowTs()));
  } catch (error) {
    console.log(error);
    yield put(syncArchivioFail(tipo, error && error.message));
    if (error && error.response && error.response.status === 401) {
      window.location.replace("/login");
    }
  }
}

function* archivioSaga() {
  yield fork(clearLegacyArchivio);
  // Una sola sincronizzazione per tipo alla volta: le richieste arrivate mentre è in corso vengono ignorate
  const running = {};
  while (true) {
    const action = yield take(SYNC_ARCHIVIO);
    const { tipo } = action.payload;
    if (running[tipo] && running[tipo].isRunning()) continue;
    running[tipo] = yield fork(syncArchivio, action);
  }
}

export default archivioSaga;
